import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Avatar,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { classService, feeCategoryService, feeClassFeeService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { getAvatarColor, getInitials } from '../../utils/avatar';

const defaultForm = {
  id: '',
  class_id: '',
  fee_category_id: '',
  amount: '',
  notes: '',
};

const AssignClassFeesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [classRes, categoryRes] = await Promise.all([classService.getAll(), feeCategoryService.getAll({ charge_scope: 'class' })]);
      if (!classRes?.success) {
        throw new Error(classRes?.error || 'Failed to load classes');
      }
      if (!categoryRes?.success) {
        throw new Error(categoryRes?.error || 'Failed to load categories');
      }
      setClasses(classRes.data || []);
      setCategories((categoryRes.data || []).filter((cat) => cat.charge_scope === 'class'));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load data';
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async (classId) => {
    if (!classId) {
      setAssignments([]);
      return;
    }
    try {
      setLoading(true);
      const response = await feeClassFeeService.getAll({ class_id: classId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load class assignments');
      }
      setAssignments(response.data || []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load class fees';
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadAssignments(selectedClass);
    }
  }, [selectedClass]);

  const selectedAssignments = useMemo(() => assignments || [], [assignments]);


  const handleOpenDialog = (assignment) => {
    if (assignment) {
      setForm({
        id: assignment.id,
        class_id: assignment.class_id,
        fee_category_id: assignment.fee_category_id,
        amount: assignment.amount ?? '',
        notes: assignment.notes ?? '',
      });
    } else {
      setForm({
        ...defaultForm,
        class_id: selectedClass,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setForm(defaultForm);
    setDialogOpen(false);
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!form.class_id || !form.fee_category_id) {
      showToast.error('Class and fee category are required');
      return;
    }
    const amountValue = Number(form.amount || 0);
    if (Number.isNaN(amountValue) || amountValue < 0) {
      showToast.error('Enter a valid amount');
      return;
    }
    const payload = {
      class_id: form.class_id,
      fee_category_id: form.fee_category_id,
      amount: amountValue,
      notes: form.notes || null,
    };
    try {
      setSaving(true);
      if (form.id) {
        await feeClassFeeService.update(form.id, payload);
        showToast.success('Class fee updated');
      } else {
        await feeClassFeeService.create(payload);
        showToast.success('Class fee assignment created');
      }
      handleCloseDialog();
      loadAssignments(selectedClass);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Save failed';
      showToast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fee assignment?')) return;
    try {
      await feeClassFeeService.delete(id);
      showToast.success('Assignment deleted');
      setAssignments((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete assignment';
      showToast.error(message);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Assign Class Fees
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Map fee categories to classes and configure standard billing details.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                  value={classes.find((cls) => cls.id === selectedClass) || null}
                  onChange={(event, newValue) => {
                    setSelectedClass(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Class"
                      placeholder="Search and select class..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />
                <Button 
                  variant="contained" 
                  disabled={!selectedClass} 
                  onClick={() => handleOpenDialog()} 
                  startIcon={<AddIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Add Assignment
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', p: { xs: 2, sm: 3 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !selectedClass ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Select a class to view and manage fee assignments.
              </Typography>
            ) : selectedAssignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No fee assignments found for this class. Click "Add Assignment" to create one.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                {selectedAssignments.map((assignment) => {
                  const category = categories.find((cat) => cat.id === assignment.fee_category_id);
                  const categoryName = category?.name || 'Category removed';
                  return (
                    <Paper
                      key={assignment.id}
                      variant="outlined"
                      sx={{
                        p: { xs: 2, sm: 2.4 },
                        borderRadius: 2,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar
                            alt={categoryName}
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: getAvatarColor(categoryName),
                              color: '#ffffff',
                              fontWeight: 700,
                            }}
                          >
                            {getInitials(categoryName)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {categoryName}
                            </Typography>
                            {category?.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {category.description}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              <strong>Amount:</strong> ₹{Number(assignment.amount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                            {assignment.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                <strong>Notes:</strong> {assignment.notes}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: { xs: '1 1 100%', md: '0 0 220px' }, minWidth: { xs: '100%', md: 180 }, flexWrap: 'wrap', rowGap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(assignment)}
                            sx={{ flex: { xs: 1, md: 'unset' } }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(assignment.id)}
                            sx={{ flex: { xs: 1, md: 'unset' } }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit Class Fee' : 'Add Class Fee'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" disabled>
              <InputLabel id="dialog-class">Class</InputLabel>
              <Select labelId="dialog-class" label="Class" value={form.class_id}>
                <MenuItem value="">
                  <em>Select class</em>
                </MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                    {cls.section ? ` - ${cls.section}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="dialog-category">Fee Category</InputLabel>
              <Select
                labelId="dialog-category"
                label="Fee Category"
                value={form.fee_category_id}
                onChange={handleChange('fee_category_id')}
              >
                <MenuItem value="">
                  <em>Select category</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              size="small"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.amount}
              onChange={handleChange('amount')}
              fullWidth
            />
            <TextField
              label="Notes"
              size="small"
              multiline
              minRows={2}
              value={form.notes}
              onChange={handleChange('notes')}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
            {form.id ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignClassFeesPage;

